import { merge, clone, cloneDeep, isEqual, isEmpty } from 'lodash';
import { DateHelper } from './date-helper';

export class ObjectHelper {
    static cloneObject<T>(obj: T, deep = false): T {
        if (deep) {
            return cloneDeep(obj);
        }

        return clone(obj);
    }

    static mergeObject<T>(target: T, source: T): T {
        const targetClone = this.cloneObject(target, true);
        return merge(targetClone, source);
    }

    static areObjectsEqual<T>(obj1: T, obj2: T): boolean {
        return isEqual(obj1, obj2);
    }

    static removeEmptyProperties<T>(obj: T): T {
        // Remove null/undefined entirely
        if (this.isNullOrUndefined(obj)) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj
                .map(item => this.removeEmptyProperties(item))
                .filter(item => !this.isNullOrUndefined(item)) as T;
        }

        // Primitives → return as-is
        if (typeof obj !== 'object') {
            return obj;
        }

        // Preserve non-plain objects (Date, Timestamp, etc.)
        if (obj?.constructor !== Object) {
            return obj;
        }

        // Plain object
        const result: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
            if (this.isNullOrUndefined(value)) {
                continue;
            }

            const item = this.removeEmptyProperties(value);

            // Skip null/undefined after cleaning
            if (this.isNullOrUndefined(item)) {
                continue;
            }

            // Skip empty plain objects
            if (
                typeof item === 'object' &&
                item?.constructor === Object &&
                Object.keys(item as Record<string, unknown>).length === 0
            ) {
                continue;
            }

            result[key] = item;
        }

        return result as T;
    }

    static fixDate<T>(obj: T, property: string): void {
        const date = (obj as any)[property];
        if (!date) {
            return;
        }

        let fixedDate: Date;
        if (typeof date === 'string') {
            fixedDate = new Date(date);
        } else {
            fixedDate = DateHelper.isTimestamp(date) ? DateHelper.convertTimestampToDate(date) : date.toDate();
        }

        (obj as any)[property] = fixedDate;
    }

    static isObjectEmpty<T>(obj: T): boolean {
        return isEmpty(obj);
    }

    // static sortObjectProperties<T>(obj: T): T {
    //     const map = new Map<string, any>();
    //     const temp = obj as any;
    //     const keys = Object.keys(temp);
    //     keys.forEach(key => {
    //         let value = temp[key];
    //         if (!DateHelper.isDate(value) && typeof value === 'object') {
    //             value = this.sortObjectProperties(value);
    //         }

    //         map.set(key, value);
    //     });
    //     const sortedMap = new Map([...map.entries()].sort());
    //     const sortedObj = Object.fromEntries(sortedMap);
    //     return sortedObj as T;
    // }

    static convertObjectToStringRecord<T extends Record<string, any>>(obj: T): Record<string, string> {
        return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, String(value)]));
    }

    private static isNullOrUndefined<T>(value: T): boolean {
        return value === null || value === undefined;
    }
}
